import {getCoolEmoji, Analytics} from 'hubbl-shared';
import {memo, useCallback, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import styled from 'styled-components';
import {Scene, WelcomeSection} from '../../../../components';
import {EventAnalyticsSummaryCards} from '../../../../components/dashboard-cards/EventAnalyticsSummaryCards';
import {DashboardTopInterests} from '../../../../components/dashboard-cards/DashboardTopInterests';
import {EmployeeOnboardingProgress} from '../../../../components/dashboard-cards/EmployeeOnboardingProgress';
import {AdminOnboardingProgress} from '../../../../components/dashboard-cards/AdminOnboardingProgress';
import {useAppSelector, useI18n} from '../../../../hooks';
import {Api} from '../../../../api';
import {UserSelector} from '../../../../store/user/UserSelector';
import {useNavigate} from 'react-router-dom';
import {AdminWebRoute} from '../../../../routes/AdminWebRoute';

const AnalyticsContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 32px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const EventAnalyticsSection = styled.div`
  /* Event analytics takes up left side */
`;

const SidebarSection = styled.div`
  /* Sidebar with multiple components */
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InterestAnalyticsSection = styled.div`
  /* Interest analytics component */
`;

const OnboardingSection = styled.div`
  /* Onboarding progress component */
`;

const AdminOnboardingSection = styled.div`
  /* Admin onboarding progress component */
  margin-bottom: 32px;
`;

export const SceneDashboard = memo(() => {
  const userDetails = useAppSelector(UserSelector.getUserDetails);
  const navigate = useNavigate();
  const location = useLocation();
  const t = useI18n();
  const [interestAnalytics, setInterestAnalytics] =
    useState<Analytics.InterestAnalytics | null>(null);

  const handleCardClick = useCallback(
    (cardType: string) => {
      // Navigate to detailed event analytics page
      navigate(`/admin/${AdminWebRoute.EventAnalytics}`);
    },
    [navigate],
  );

  // Check if user just completed get-started flow
  useEffect(() => {
    if (location.state?.showWelcomeTutorial) {
      // TODO: Implement IntroJS tutorial
      // For now, just show a welcome alert
      // In the future, import intro.js and configure the tutorial steps
      console.log('Welcome to Hubbl! Tutorial will be shown here.');

      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Load interest analytics data
  useEffect(() => {
    const loadInterestAnalytics = async () => {
      if (!userDetails?.companyId) return;

      try {
        const analytics = await Api.interestAnalytics.getInterestAnalytics(
          userDetails.companyId,
        );
        setInterestAnalytics(analytics);
      } catch (error) {
        console.error('Error loading interest analytics:', error);
      }
    };

    loadInterestAnalytics();
  }, [userDetails?.companyId]);

  return (
    <Scene>
      <WelcomeSection
        title={`${t.dashboard.welcome}, ${userDetails?.firstName} 👋`}
        description={`${t.dashboard.welcomeDescription} ${userDetails?.companyName} ${getCoolEmoji()}`}
      />

      {/* Admin Onboarding Progress - Always visible */}
      <AdminOnboardingSection>
        <AdminOnboardingProgress />
      </AdminOnboardingSection>

      {/* Analytics Side-by-Side Layout */}
      <AnalyticsContainer>
        <EventAnalyticsSection>
          <EventAnalyticsSummaryCards onCardClick={handleCardClick} />
        </EventAnalyticsSection>

        <SidebarSection>
          <OnboardingSection>
            <EmployeeOnboardingProgress />
          </OnboardingSection>

          <InterestAnalyticsSection>
            {interestAnalytics ? (
              <DashboardTopInterests analytics={interestAnalytics} />
            ) : (
              <div
                style={{padding: '40px', textAlign: 'center', color: '#999'}}>
                {t.common.loadingInterests}
              </div>
            )}
          </InterestAnalyticsSection>
        </SidebarSection>
      </AnalyticsContainer>
    </Scene>
  );
});
